import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';
import { EvaluationColor } from '@repo/types/SubMetricEvaluation';
import { useSuspenseQuery } from '@tanstack/react-query';
import { API } from 'api';

import { apiBasePath, apiName } from 'assets';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';

export type PreviousMetricPoint = {
  user_value?: number;
  evaluation_color: EvaluationColor;
};

// Keyed by submetric name.
export type PreviousMetricValues = Record<string, PreviousMetricPoint[]>;

export type PreviousMetricValuesResponse = {
  previousValues: PreviousMetricValues;
  previousKitCount: number;
  versionExcludedNames: string[];
};

export const getPreviousMetricValues = async (kitId: string) => {
  const res = await API.get(
    apiName,
    `${apiBasePath}/results/previous-metric-values/${kitId}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.METRICS,
        importance: IMPORTANCE.HIGH,
      },
      context: {
        kitId,
      },
    },
  );
  return {
    previousValues: res.previousValues ?? {},
    previousKitCount: res.previousKitCount ?? 0,
    versionExcludedNames: res.versionExcludedNames ?? [],
  } as PreviousMetricValuesResponse;
};

const EMPTY_RESPONSE: PreviousMetricValuesResponse = {
  previousValues: {},
  previousKitCount: 0,
  versionExcludedNames: [],
};

export const getPreviousMetricValuesQuery = (kitId?: string) => ({
  queryKey: [apiName, `previousMetricValues`, kitId],
  queryFn: () => (kitId ? getPreviousMetricValues(kitId) : EMPTY_RESPONSE),
});

const usePreviousMetricValues = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const previousValuesQuery = useSuspenseQuery<PreviousMetricValuesResponse>(
    getPreviousMetricValuesQuery(currentKitId),
  );

  return previousValuesQuery;
};

export default usePreviousMetricValues;
